import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  /** @type {MutationObserver | undefined} */
  #pageStatusObserver;

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');
    this.#updatePageStatus();
    const grid = this.querySelector('[ref="grid"]');
    if (grid) {
      this.#pageStatusObserver = new MutationObserver(this.#updatePageStatus);
      this.#pageStatusObserver.observe(grid, { childList: true });
    }
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    this.#pageStatusObserver?.disconnect();
  }

  #updatePageStatus = () => {
    const grid = this.querySelector('[ref="grid"]');
    const status = this.querySelector('[data-page-status]');
    if (!(grid instanceof HTMLElement) || !(status instanceof HTMLElement)) return;
    const total = Number(grid.dataset.lastPage || 1);
    const loadedPages = Array.from(grid.querySelectorAll(':scope > [data-page]'), (card) => Number(card.dataset.page));
    const current = loadedPages.length ? Math.max(...loadedPages) : 1;
    const currentLabel = status.querySelector('[data-page-current]');
    const totalLabel = status.querySelector('[data-page-total]');
    const spinner = status.querySelector('[data-page-spinner]');
    if (currentLabel) currentLabel.textContent = String(current);
    if (totalLabel) totalLabel.textContent = String(total);
    if (spinner instanceof HTMLElement) spinner.hidden = current >= total;
    status.hidden = total <= 1;
    status.setAttribute('aria-label', `Showing ${current} of ${total} pages`);
  };

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}